import React, { useEffect, useMemo, useState } from 'react';

/**
 * DateIntervalSelector
 *
 * Props:
 *  - start, end: Date | string (ISO) | null  -> controlled values (optional)
 *  - defaultStart, defaultEnd: Date | string -> initial values when uncontrolled (optional)
 *  - onChange: (start: Date, end: Date) => void
 *  - min, max: Date | string (optional) -> constrain inputs
 *  - disabled: boolean (optional)
 *  - className, style: for outer wrapper (optional)
 *  - presets: array of { label: string, days: number } (optional; defaults to 1/3/7/30)
 *
 * Notes:
 *  - Uses <input type="datetime-local"> (local time, no timezone).
 *  - If uncontrolled, keeps start <= end by adjusting the other end when necessary.
 */
export default function DateIntervalSelector({
  interval,
  onChange,
  min,
  max,
  disabled = false,
  className,
  style,
  presets,
}) {
  // Helpers
  const coerceDate = (v, fallback = null) => {
    if (v == null) return fallback;
    if (v instanceof Date) return v;
    const d = new Date(v);
    return isNaN(d) ? fallback : d;
  };

  const pad = (n) => String(n).padStart(2, '0');

  // Round to minute (html datetime-local typically ignores seconds)
  const roundToMinute = (d) => {
    const x = new Date(d);
    x.setSeconds(0, 0);
    return x;
  };

  const toInputValue = (d) =>
    d
      ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
          d.getHours()
        )}:${pad(d.getMinutes())}`
      : '';

  const fromInputValue = (str) => {
    // Interpreted as LOCAL time by the browser when no timezone suffix is present
    const d = new Date(str);
    return isNaN(d) ? null : roundToMinute(d);
  };

  const nowRounded = () => roundToMinute(new Date());

  // Controlled vs uncontrolled
  const controlledStart = interval.start != null;
  const controlledEnd = interval.end != null;

  const [internalStart, setInternalStart] = useState(() =>
    coerceDate(null, roundToMinute(new Date(Date.now() - 24 * 60 * 60 * 1000)))
  );
  const [internalEnd, setInternalEnd] = useState(() => coerceDate(null, nowRounded()));

  // Derive effective values
  const startDate = controlledStart ? coerceDate(interval.start, null) : internalStart;
  const endDate = controlledEnd ? coerceDate(interval.end, null) : internalEnd;

  // Sync internal state when controlled props change
  useEffect(() => {
    if (controlledStart && startDate) setInternalStart(startDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledStart, toInputValue(startDate)]);

  useEffect(() => {
    if (controlledEnd && endDate) setInternalEnd(endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledEnd, toInputValue(endDate)]);

  // Min/Max coercion
  const minDate = useMemo(() => coerceDate(min), [min]);
  const maxDate = useMemo(() => coerceDate(max), [max]);

  const emitChange = (s, e) => {
    if (onChange) onChange({start: s, end: e});
  };

  // Keep order (only when uncontrolled; controlled mode leaves it to parent)
  const setStartUncontrolled = (d) => {
    if (controlledStart) return emitChange(d ?? startDate, endDate);
    if (!d) return;
    if (endDate && d > endDate) {
      setInternalStart(d);
      setInternalEnd(d);
      emitChange(d, d);
    } else {
      setInternalStart(d);
      emitChange(d, endDate);
    }
  };

  const setEndUncontrolled = (d) => {
    if (controlledEnd) return emitChange(startDate, d ?? endDate);
    if (!d) return;
    if (startDate && d < startDate) {
      setInternalStart(d);
      setInternalEnd(d);
      emitChange(d, d);
    } else {
      setInternalEnd(d);
      emitChange(startDate, d);
    }
  };

  const applyPresetDays = (hours) => {

    let e = nowRounded();
    let s = roundToMinute(new Date(e.getTime() - hours * 60 * 60 * 1000));
    if (hours < 0){
        s = roundToMinute(new Date(e.getTime() - 5 * 24 * 60 * 60 * 1000));
        e = roundToMinute(new Date(e.getTime() - hours * 60 * 60 * 1000));
    }
    if (!controlledStart) setInternalStart(s);
    if (!controlledEnd) setInternalEnd(e);
    emitChange(s, e);
  };

  // Constrain to min/max in UI only
  const minStr = minDate ? toInputValue(minDate) : undefined;
  const maxStr = maxDate ? toInputValue(maxDate) : undefined;

  const effectivePresets =
    presets && presets.length
      ? presets
      : [
          { label: '1 hour', hours: 1 },
          { label: '3 hours', hours: 3 },
          { label: '6 hours', hours: 6 },
          { label: '12 hours', hours: 12 },
          { label: '1 day', hours: 24 },
          { label: '3 days', hours: 72 },
          { label: '7 days', hours: 168 },
          { label: '30 days', hours: 720 },
          { label: '3 month', hours: 2160 },
          { label: '6 month', hours: 4320 },
          { label: '1 year', hours: 8760 },
          { label: '+10 days', hours: -240 },
        ];

  return (
    <div className={className} style={{ display: 'grid', gap: 8, ...style }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {effectivePresets.map((p) => (
          <button
            key={p.label}
            type="button"
            disabled={disabled}
            onClick={() => applyPresetDays(p.hours)}
            style={{
              padding: '6px 10px',
              borderRadius: 8,
              border: '1px solid #ccc',
              background: disabled ? '#f2f2f2' : '#fff',
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr' }}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span>Start</span>
          <input
            type="datetime-local"
            value={startDate ? toInputValue(startDate) : ''}
            min={minStr}
            max={maxStr}
            onChange={(e) => {
              const d = fromInputValue(e.target.value);
              if (!d) return;
              if (controlledStart) emitChange(d, endDate);
              else setStartUncontrolled(d);
            }}
            disabled={disabled}
          />
        </label>

        <label style={{ display: 'grid', gap: 4 }}>
          <span>End</span>
          <input
            type="datetime-local"
            value={endDate ? toInputValue(endDate) : ''}
            min={minStr}
            max={maxStr}
            onChange={(e) => {
              const d = fromInputValue(e.target.value);
              if (!d) return;
              if (controlledEnd) emitChange(startDate, d);
              else setEndUncontrolled(d);
            }}
            disabled={disabled}
          />
        </label>
      </div>
    </div>
  );
}
